import { useUiStore } from '@/stores/ui'

import type { IAction } from '@/types/api/IAction'

export function useUi() {
  const {
    storeAddToActionsInProgress,
    storeRemoveFromActionsInProgress,
    storeAddToActionsInSuccess,
    storeAddToActionsInError,
    storeResetActionsInError,
  } = useUiStore()

  const handleActionAttempt = (action: IAction) => {
    storeResetActionsInError()
    storeAddToActionsInProgress(action)
  }

  const handleActionError = (action: IAction, e: unknown) => {
    storeAddToActionsInError(action, e)
    storeRemoveFromActionsInProgress(action)
  }

  const handleActionSuccess = (action: IAction, successMessage: string | null = null) => {
    storeAddToActionsInSuccess(action, successMessage)
    storeRemoveFromActionsInProgress(action)
  }

  /**
   * This methods takes a form element and a callback function.
   * It will validate the form and if it is valid, it will call the callback function.
   * If the form is invalid, it will scroll to the first invalid input and report the error.
   * ### IMPORTANT ###
   * This method is only meant to be used with forms in the right drawer.
   * It allows for the sticky header, which is 88px high, to be taken into account when
   * scrolling to the first invalid input.
   * This is fine, as the majority of CRUD operations will take place in the
   * RightDrawer component for this app.
   */
  const validateForm = async <T>(formElement: HTMLFormElement | null, callback: any): Promise<T | void> => {
    if (formElement) {
      const inputs = formElement.querySelectorAll('input, select, textarea')
      let isValid = true

      const rightDrawer = document.querySelector('#right-drawer')

      if (rightDrawer) {
        for (let i = 0; i < inputs.length; i++) {
          const input = inputs[i] as HTMLInputElement
          if (!input.checkValidity()) {
            const scrollToYPosition = input.getBoundingClientRect().top - rightDrawer.getBoundingClientRect().top + rightDrawer.scrollTop - 88
            rightDrawer.scrollTo({
              top: scrollToYPosition,
            })
            isValid = false
            setTimeout(() => {
              input.reportValidity()
            }, 50)
            break // Break out of the loop on the first invalid input
          }
        }
      }
      else {
        for (let i = 0; i < inputs.length; i++) {
          const input = inputs[i] as HTMLInputElement
          if (!input.checkValidity()) {
            isValid = false
            input.reportValidity()
            break // Break out of the loop on the first invalid input
          }
        }
      }

      if (isValid) {
        return await callback()
      }
    }
  }

  const uiActions = {
    bootingApp: {
      id: 'bootingApp',
      successMessage: 'Successfully booted app.',
      errorMessage: 'There was an error booting the app.',
    } as IAction,
  }

  return {
    handleActionError,
    handleActionSuccess,
    validateForm,
    uiActions,
    handleActionAttempt,
  }
}
